import axios from 'axios';
import { getTokens, setTokens, logout } from './utils/auth';
import { useToast } from './composables/useToast';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.ci.adata-ai.com/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = getTokens();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const { showErrorToast } = useToast();

    // Handle 401 Unauthorized responses
    if (error.response && error.response.status === 401) {
      console.log('🚫 Received 401 Unauthorized response');
      
      // If this is already a retry attempt or a refresh request, logout immediately
      if (originalRequest._retry || originalRequest.url.includes('/auth/refresh')) {
        console.log('🚫 Retry failed or refresh token invalid, logging out...');
        await logout();
        showErrorToast('Session expired or invalid. Please log in again.');
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const { refreshToken } = getTokens();
      if (!refreshToken) {
        console.log('🚫 No refresh token available, logging out...');
        isRefreshing = false;
        await logout();
        showErrorToast('No active session found. Please log in.');
        return Promise.reject(error);
      }

      try {
        console.log('🔄 Attempting to refresh token...');
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
        const { access_token, refresh_token } = res.data;
        
        console.log('✅ Token refreshed successfully');
        setTokens(access_token, refresh_token);
        processQueue(null, access_token);
        
        // Retry the original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.log('🚫 Token refresh failed, logging out...', refreshError);
        processQueue(refreshError, null);
        await logout();
        showErrorToast('Failed to refresh session. Please log in again.');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, extract a more specific message
    let errorMessage = 'An unexpected error occurred.';
    if (error.response) {
      // Attempt to get a specific message from the backend response
      if (error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status) {
        errorMessage = `Error: ${error.response.status} - ${error.response.statusText || 'Server Error'}`;
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'Network error: No response from server. Please check your internet connection.';
    } else {
      // Something happened in setting up the request that triggered an Error
      errorMessage = `Request error: ${error.message}`;
    }

    // Show error toast for non-401 errors
    showErrorToast(errorMessage);

    // Log the full error for debugging
    console.error('API call failed:', error);

    // Reject with a more informative error object
    return Promise.reject({
      message: errorMessage,
      originalError: error,
    });
  }
);

export const getMarketPriceOverview = async () => {
  try {
    const response = await apiClient.get('/ci/price_overview');
    return response.data;
  } catch (error) {
    console.error('Error fetching market price overview:', error);
    throw error;
  }
};

export const getBrandMarketPerformanceSummary = async () => {
  try {
    const response = await apiClient.get('/ci/market_performances');
    return response.data;
  } catch (error) {
    console.error('Error fetching brand market performance summary:', error);
    throw error;
  }
};

export default apiClient;
